import React, { useState, useEffect } from 'react';
import { automunityAPI } from '../lib/api';

interface Opportunity {
  opportunity_id: number;
  title: string;
  description: string;
  how_it_works: string;
  accessibility: string;
  frequency: string;
  impact: string;
  status: string;
  source_document: string;
  data_source: string;
  source_display_name?: string | null;
  team_name?: string | null;
  team_abbreviation?: string | null;
  team_id?: number | null;
  tags?: Tag[];
  discovered_at: string;
  reviewer_name: string | null;
  reviewed_at: string | null;
  dismiss_reason: string | null;
  comments?: any[];
  status_history?: any[];
}

interface Stats {
  total_opportunities: number;
  by_status: Record<string, number>;
  by_impact: Record<string, number>;
  by_accessibility: Record<string, number>;
}

interface Team {
  team_id: number;
  name: string;
  abbreviation?: string | null;
}

interface Tag {
  tag_id: number;
  name: string;
  slug: string;
  color?: string | null;
  usage_count?: number;
}

const IMPACT_COLORS: Record<string, string> = { high: '#28a745', medium: '#ffc107', low: '#6c757d' };
const STATUS_COLORS: Record<string, string> = { new: '#007bff', needs_discussion: '#fd7e14', approved: '#28a745', dismissed: '#dc3545', promoted: '#6f42c1' };
const ACCESSIBILITY_LABELS: Record<string, string> = { cloud_aws: 'Cloud (AWS)', cloud_hybrid: 'Cloud (Hybrid)', local_automate: 'Local (Automate)', local_powershell: 'Local (PowerShell)' };

const AiOpportunities: React.FC = () => {
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedOpp, setSelectedOpp] = useState<Opportunity | null>(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterImpact, setFilterImpact] = useState('');
  const [filterTeam, setFilterTeam] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [teams, setTeams] = useState<Team[]>([]);
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [newTag, setNewTag] = useState('');
  const [comment, setComment] = useState('');
  const [statusNote, setStatusNote] = useState('');

  // Load teams + tags once for the filter controls.
  useEffect(() => {
    (async () => {
      try {
        const [teamsRes, tagsRes] = await Promise.all([
          automunityAPI.getTeams(),
          automunityAPI.getAiOpportunityTags(),
        ]);
        const teamsData = await teamsRes.body.json();
        const tagsData = await tagsRes.body.json();
        const teamsParsed = typeof teamsData === 'string' ? JSON.parse(teamsData) : teamsData;
        const tagsParsed = typeof tagsData === 'string' ? JSON.parse(tagsData) : tagsData;
        setTeams(Array.isArray(teamsParsed) ? teamsParsed : (teamsParsed.teams || []));
        setAllTags(tagsParsed.tags || []);
      } catch (error) {
        console.error('Error loading teams/tags:', error);
      }
    })();
  }, []);

  // Debounce the free-text search so we don't refetch on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setSearchQuery(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => { fetchData(); }, [filterStatus, filterImpact, filterTeam, selectedTags, searchQuery]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (filterStatus) params.status = filterStatus;
      if (filterImpact) params.impact = filterImpact;
      if (filterTeam) params.team_id = filterTeam;
      if (selectedTags.length) params.tags = selectedTags.join(',');
      if (searchQuery) params.q = searchQuery;

      const [oppRes, statsRes] = await Promise.all([
        automunityAPI.getAiOpportunities(params),
        automunityAPI.getAiOpportunityStats()
      ]);

      const oppData = await oppRes.body.json();
      const statsData = await statsRes.body.json();

      const oppParsed = typeof oppData === 'string' ? JSON.parse(oppData) : oppData;
      const statsParsed = typeof statsData === 'string' ? JSON.parse(statsData) : statsData;

      setOpportunities(oppParsed.opportunities || []);
      setTotalCount(typeof oppParsed.total === 'number' ? oppParsed.total : null);
      setStats(statsParsed);
    } catch (error) {
      console.error('Error fetching AI opportunities:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleTag = (slug: string) => {
    setSelectedTags(prev => prev.includes(slug) ? prev.filter(s => s !== slug) : [...prev, slug]);
  };

  const refreshTags = async () => {
    try {
      const res = await automunityAPI.getAiOpportunityTags();
      const data = await res.body.json();
      const parsed = typeof data === 'string' ? JSON.parse(data) : data;
      setAllTags(parsed.tags || []);
    } catch (error) {
      console.error('Error refreshing tags:', error);
    }
  };

  const handleAddTag = async (oppId: number) => {
    if (!newTag.trim()) return;
    try {
      await automunityAPI.addAiOpportunityTag(oppId, newTag.trim());
      setNewTag('');
      loadOpportunityDetail(oppId);
      fetchData();
      refreshTags();
    } catch (error) {
      console.error('Error adding tag:', error);
    }
  };

  const handleRemoveTag = async (oppId: number, tagId: number) => {
    try {
      await automunityAPI.removeAiOpportunityTag(oppId, tagId);
      loadOpportunityDetail(oppId);
      fetchData();
      refreshTags();
    } catch (error) {
      console.error('Error removing tag:', error);
    }
  };

  const handleStatusChange = async (oppId: number, newStatus: string) => {
    try {
      await automunityAPI.updateAiOpportunity(oppId, { status: newStatus, note: statusNote });
      setStatusNote('');
      fetchData();
      if (selectedOpp?.opportunity_id === oppId) {
        loadOpportunityDetail(oppId);
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleAddComment = async (oppId: number) => {
    if (!comment.trim()) return;
    try {
      await automunityAPI.addAiOpportunityComment(oppId, comment);
      setComment('');
      loadOpportunityDetail(oppId);
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const loadOpportunityDetail = async (oppId: number) => {
    try {
      const res = await automunityAPI.getAiOpportunity(oppId);
      const data = await res.body.json();
      const parsed = typeof data === 'string' ? JSON.parse(data) : data;
      setSelectedOpp(parsed);
    } catch (error) {
      console.error('Error loading opportunity detail:', error);
    }
  };

  const handlePromote = async (oppId: number) => {
    try {
      await automunityAPI.promoteAiOpportunity(oppId, {});
      fetchData();
      setSelectedOpp(null);
    } catch (error) {
      console.error('Error promoting opportunity:', error);
    }
  };

  if (loading) return <div className="loading">Loading AI Opportunities...</div>;

  // Group opportunities by their source's friendly label (e.g. "SPG"), falling
  // back to the raw data_source. Order preserved from the API (newest first);
  // groups ordered largest first.
  const groupedOpportunities: [string, Opportunity[]][] = (() => {
    const groups: Record<string, Opportunity[]> = {};
    for (const opp of opportunities) {
      const label = opp.source_display_name || opp.data_source || 'Unattributed';
      (groups[label] ||= []).push(opp);
    }
    return Object.entries(groups).sort((a, b) => b[1].length - a[1].length);
  })();

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ margin: '0 0 8px', fontSize: '24px' }}>AI Automation Opportunities</h1>
        <p style={{ margin: 0, color: '#666' }}>AI-discovered automation opportunities from SPG OneNote documentation. Review, discuss, and promote to real requests.</p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', marginBottom: '24px' }}>
          <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '16px', textAlign: 'center' }}>
            <div style={{ fontSize: '28px', fontWeight: 700 }}>{stats.total_opportunities}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>Total</div>
          </div>
          {Object.entries(stats.by_status || {}).map(([status, count]) => (
            <div key={status} style={{ background: '#f8f9fa', borderRadius: '8px', padding: '16px', textAlign: 'center' }}>
              <div style={{ fontSize: '28px', fontWeight: 700, color: STATUS_COLORS[status] || '#333' }}>{count}</div>
              <div style={{ fontSize: '12px', color: '#666', textTransform: 'capitalize' }}>{status.replace('_', ' ')}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search title, description..."
          style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd', flex: '1 1 240px', minWidth: '200px' }}
        />
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd' }}>
          <option value="">All Statuses</option>
          <option value="new">New</option>
          <option value="needs_discussion">Needs Discussion</option>
          <option value="approved">Approved</option>
          <option value="dismissed">Dismissed</option>
          <option value="promoted">Promoted</option>
        </select>
        <select value={filterImpact} onChange={(e) => setFilterImpact(e.target.value)} style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd' }}>
          <option value="">All Impact</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
        <select value={filterTeam} onChange={(e) => setFilterTeam(e.target.value)} style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd' }}>
          <option value="">All Teams</option>
          {teams.map(t => (
            <option key={t.team_id} value={String(t.team_id)}>{t.name}</option>
          ))}
        </select>
      </div>

      {/* Tag filter chips (match ANY selected tag) */}
      {allTags.length > 0 && (
        <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: '12px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Tags</span>
          {allTags.map(tag => {
            const active = selectedTags.includes(tag.slug);
            return (
              <button
                key={tag.tag_id}
                onClick={() => toggleTag(tag.slug)}
                style={{
                  padding: '4px 10px', borderRadius: '14px', fontSize: '12px', cursor: 'pointer',
                  border: active ? '1px solid #007bff' : '1px solid #d1d5db',
                  background: active ? '#007bff' : '#fff',
                  color: active ? '#fff' : '#475569',
                }}
              >
                {tag.name}{typeof tag.usage_count === 'number' ? ` (${tag.usage_count})` : ''}
              </button>
            );
          })}
          {selectedTags.length > 0 && (
            <button onClick={() => setSelectedTags([])} style={{ padding: '4px 10px', borderRadius: '14px', fontSize: '12px', cursor: 'pointer', border: 'none', background: 'transparent', color: '#dc3545' }}>Clear</button>
          )}
        </div>
      )}

      {/* Result count */}
      <div style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>
        {totalCount != null && totalCount > opportunities.length
          ? `Showing ${opportunities.length} of ${totalCount}`
          : `${opportunities.length} result${opportunities.length === 1 ? '' : 's'}`}
      </div>

      {/* Opportunities List */}
      <div style={{ display: 'grid', gap: '16px' }}>
        {groupedOpportunities.map(([groupLabel, groupOpps]) => (
          <div key={groupLabel}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '8px 0 12px' }}>
              <h3 style={{ margin: 0, fontSize: '15px', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#334155' }}>{groupLabel}</h3>
              <span style={{ background: '#e2e8f0', color: '#475569', padding: '2px 8px', borderRadius: '10px', fontSize: '12px', fontWeight: 600 }}>{groupOpps.length}</span>
            </div>
            <div style={{ display: 'grid', gap: '16px', marginBottom: '24px' }}>
              {groupOpps.map(opp => (
                <div key={opp.opportunity_id}
                  style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '20px', cursor: 'pointer', transition: 'box-shadow 0.2s' }}
                  onClick={() => loadOpportunityDetail(opp.opportunity_id)}
                  onMouseOver={(e) => (e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)')}
                  onMouseOut={(e) => (e.currentTarget.style.boxShadow = 'none')}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <h3 style={{ margin: 0, fontSize: '16px' }}>{opp.title}</h3>
                        <span style={{ background: IMPACT_COLORS[opp.impact] || '#666', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>{opp.impact.toUpperCase()}</span>
                        <span style={{ background: STATUS_COLORS[opp.status] || '#666', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>{opp.status.replace('_', ' ')}</span>
                      </div>
                      <p style={{ margin: '0 0 8px', color: '#555', fontSize: '14px' }}>{opp.description}</p>
                      <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: '#888' }}>
                        <span>{ACCESSIBILITY_LABELS[opp.accessibility] || opp.accessibility}</span>
                        <span>{opp.frequency}</span>
                        <span>{new Date(opp.discovered_at).toLocaleDateString()}</span>
                      </div>
                      {opp.tags && opp.tags.length > 0 && (
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '8px' }}>
                          {opp.tags.map(tag => (
                            <span key={tag.tag_id} style={{ background: '#eef2ff', color: '#4338ca', padding: '2px 8px', borderRadius: '10px', fontSize: '11px' }}>{tag.name}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
        {opportunities.length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px', color: '#888' }}>
            <p style={{ fontSize: '18px' }}>No opportunities found</p>
            <p>AI discovery runs daily at 3 AM UTC. Adjust filters to see more.</p>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedOpp && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setSelectedOpp(null)}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '32px', maxWidth: '700px', width: '90%', maxHeight: '80vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
              <h2 style={{ margin: 0 }}>{selectedOpp.title}</h2>
              <button onClick={() => setSelectedOpp(null)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>x</button>
            </div>

            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
              <span style={{ background: IMPACT_COLORS[selectedOpp.impact], color: '#fff', padding: '3px 10px', borderRadius: '4px', fontSize: '12px', fontWeight: 600 }}>{selectedOpp.impact.toUpperCase()} IMPACT</span>
              <span style={{ background: '#e2e8f0', padding: '3px 10px', borderRadius: '4px', fontSize: '12px' }}>{ACCESSIBILITY_LABELS[selectedOpp.accessibility]}</span>
              <span style={{ background: '#e2e8f0', padding: '3px 10px', borderRadius: '4px', fontSize: '12px' }}>{selectedOpp.frequency}</span>
              {selectedOpp.team_name && (
                <span style={{ background: '#dcfce7', color: '#166534', padding: '3px 10px', borderRadius: '4px', fontSize: '12px' }}>{selectedOpp.team_name}</span>
              )}
            </div>

            {/* Tags */}
            <div style={{ marginBottom: '16px' }}>
              <h4 style={{ margin: '0 0 8px', color: '#555' }}>Tags</h4>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                {(selectedOpp.tags || []).map(tag => (
                  <span key={tag.tag_id} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#eef2ff', color: '#4338ca', padding: '3px 8px', borderRadius: '10px', fontSize: '12px' }}>
                    {tag.name}
                    <button onClick={() => handleRemoveTag(selectedOpp.opportunity_id, tag.tag_id)} title="Remove tag"
                      style={{ background: 'none', border: 'none', color: '#6366f1', cursor: 'pointer', fontSize: '13px', lineHeight: 1, padding: 0 }}>x</button>
                  </span>
                ))}
                <input
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddTag(selectedOpp.opportunity_id)}
                  placeholder="Add tag..."
                  style={{ padding: '3px 8px', borderRadius: '10px', border: '1px solid #d1d5db', fontSize: '12px', width: '110px' }}
                />
                <button onClick={() => handleAddTag(selectedOpp.opportunity_id)} style={{ padding: '3px 10px', borderRadius: '10px', border: 'none', background: '#4338ca', color: '#fff', fontSize: '12px', cursor: 'pointer' }}>Add</button>
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <h4 style={{ margin: '0 0 6px', color: '#555' }}>What's done manually:</h4>
              <p style={{ margin: 0, lineHeight: 1.6 }}>{selectedOpp.description}</p>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <h4 style={{ margin: '0 0 6px', color: '#555' }}>How it could be automated:</h4>
              <p style={{ margin: 0, lineHeight: 1.6 }}>{selectedOpp.how_it_works}</p>
            </div>

            <div style={{ marginBottom: '20px', fontSize: '13px', color: '#888' }}>
              <span>Source: {selectedOpp.source_document}</span> | <span>Discovered: {new Date(selectedOpp.discovered_at).toLocaleString()}</span>
            </div>

            {/* Status Actions */}
            <div style={{ borderTop: '1px solid #eee', paddingTop: '16px', marginBottom: '16px' }}>
              <h4 style={{ margin: '0 0 8px' }}>Actions</h4>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {selectedOpp.status !== 'approved' && <button onClick={() => handleStatusChange(selectedOpp.opportunity_id, 'approved')} style={{ padding: '8px 16px', background: '#28a745', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Approve</button>}
                {selectedOpp.status !== 'needs_discussion' && <button onClick={() => handleStatusChange(selectedOpp.opportunity_id, 'needs_discussion')} style={{ padding: '8px 16px', background: '#fd7e14', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Needs Discussion</button>}
                {selectedOpp.status !== 'dismissed' && <button onClick={() => handleStatusChange(selectedOpp.opportunity_id, 'dismissed')} style={{ padding: '8px 16px', background: '#dc3545', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Dismiss</button>}
                {selectedOpp.status !== 'promoted' && <button onClick={() => handlePromote(selectedOpp.opportunity_id)} style={{ padding: '8px 16px', background: '#6f42c1', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Promote to Request</button>}
              </div>
            </div>

            {/* Comments */}
            <div style={{ borderTop: '1px solid #eee', paddingTop: '16px' }}>
              <h4 style={{ margin: '0 0 12px' }}>Comments ({selectedOpp.comments?.length || 0})</h4>
              {(selectedOpp.comments || []).map((c: any) => (
                <div key={c.comment_id} style={{ background: '#f8f9fa', borderRadius: '6px', padding: '12px', marginBottom: '8px' }}>
                  <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px' }}><strong>{c.user_name || 'Unknown'}</strong> - {new Date(c.created_at).toLocaleString()}</div>
                  <div style={{ fontSize: '14px' }}>{c.comment}</div>
                </div>
              ))}
              <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                <input type="text" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Add a comment..." style={{ flex: 1, padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px' }} onKeyDown={(e) => e.key === 'Enter' && handleAddComment(selectedOpp.opportunity_id)} />
                <button onClick={() => handleAddComment(selectedOpp.opportunity_id)} style={{ padding: '8px 16px', background: '#007bff', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Post</button>
              </div>
            </div>

            {/* Status History */}
            {(selectedOpp.status_history || []).length > 0 && (
              <div style={{ borderTop: '1px solid #eee', paddingTop: '16px', marginTop: '16px' }}>
                <h4 style={{ margin: '0 0 8px' }}>Status History</h4>
                {(selectedOpp.status_history || []).map((s: any) => (
                  <div key={s.log_id} style={{ fontSize: '13px', color: '#666', marginBottom: '4px' }}>
                    <span>{s.changed_by_name || 'System'}</span> changed status from <strong>{s.previous_status}</strong> to <strong>{s.new_status}</strong> - {new Date(s.created_at).toLocaleString()}
                    {s.note && <span style={{ fontStyle: 'italic' }}> — "{s.note}"</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AiOpportunities;
